<template>
  <v-tooltip v-if="tooltip" top>
    <template v-slot:activator="{ on }">
      <v-btn
        v-bind="$attrs"
        v-on="on"
        class="custom-button"
        :rounded="rounded"
        :block="block"
        :color="buttonColor"
        :class="buttonClasses"
        :depressed="depressed"
        :disabled="disabled"
        :elevation="elevation"
        :fab="fab"
        :href="href"
        :icon="Boolean(icon) && !fab && !text"
        :id="id"
        :large="size === SIZES.large"
        :loading="loading"
        :outlined="outlined"
        :ripple="!plain"
        :small="size === SIZES.small"
        :target="target"
        :text="plain"
        :to="to"
        :x-large="size === SIZES['x-large']"
        :x-small="size === SIZES['x-small']"
        @mouseleave="$emit('mouseleave', $event)"
        @mouseenter="$emit('mouseenter', $event)"
        @click="$emit('click', $event)"
        style="pointer-events: all;"
      >
        <v-icon
          v-if="icon && iconPosition === ICON_POSITIONS.left"
          :class="{ 'me-1': Boolean(text) }"
          :color="textColor"
          :small="size === SIZES.small"
          :large="size === SIZES.large"
          :x-large="size === SIZES['x-large']"
          :x-small="size === SIZES['x-small']"
        >
          {{ icon }}
        </v-icon>

        <span
          v-if="text && !loading"
          class="button-text"
          :class="[{ 'text-truncate': truncate }, textColorClass]"
          :style="{ 'max-width': maxWidth && maxWidth + 'px' }"
        >
          {{ text }}
        </span>

        <v-icon
          v-if="icon && iconPosition === ICON_POSITIONS.right"
          class="ms-2"
          :color="textColor"
        >
          {{ icon }}
        </v-icon>
      </v-btn>
    </template>

    <span>{{ tooltip }}</span>
  </v-tooltip>

  <v-btn
    v-else
    v-bind="$attrs"
    class="custom-button"
    :rounded="rounded"
    :block="block"
    :color="buttonColor"
    :class="buttonClasses"
    :depressed="depressed"
    :disabled="disabled"
    :elevation="elevation"
    :fab="fab"
    :href="href"
    :icon="Boolean(icon) && !fab && !text"
    :id="id"
    :large="size === SIZES.large"
    :loading="loading"
    :outlined="outlined"
    :ripple="!plain"
    :small="size === SIZES.small"
    :target="target"
    :text="plain"
    :to="to"
    :x-large="size === SIZES['x-large']"
    :x-small="size === SIZES['x-small']"
    @mouseleave="$emit('mouseleave', $event)"
    @mouseenter="$emit('mouseenter', $event)"
    @click="$emit('click', $event)"
  >
    <v-icon
      v-if="icon && iconPosition === ICON_POSITIONS.left"
      :class="{ 'me-1': Boolean(text) }"
      :color="textColor"
      :small="size === SIZES.small"
      :large="size === SIZES.large"
      :x-large="size === SIZES['x-large']"
      :x-small="size === SIZES['x-small']"
    >
      {{ icon }}
    </v-icon>

    <span
      v-if="text && !loading"
      class="button-text"
      :class="[{ 'text-truncate': truncate }, textColorClass]"
      :style="{ 'max-width': maxWidth && maxWidth + 'px' }"
    >
      {{ text }}
    </span>

    <v-icon
      v-if="icon && iconPosition === ICON_POSITIONS.right"
      class="ms-2"
      :color="textColor"
    >
      {{ icon }}
    </v-icon>
  </v-btn>
</template>

<script>
export const COLORS = Object.freeze({
  primary: {
    bg: 'primary',
    text: 'white',
  },
  primaryLighten: {
    bg: 'blue lighten-2',
    text: 'primary',
  },
  secondary: {
    bg: 'secondary',
    text: 'white',
  },
  grey: {
    bg: 'blue-grey',
    text: 'blue-grey darken-3',
  },
  danger: {
    bg: 'red',
    text: 'white',
  },
  warning: {
    bg: 'yellow',
    text: 'white',
  },
  green: {
    bg: 'primary green',
    text: 'white',
  },
  white: {
    bg: 'white',
    text: 'blue-grey darken-1',
  },
  black: {
    bg: 'black',
    text: 'white',
  },
  default: {
    bg: 'primary',
    text: 'white',
  },
  blueGrey: {
    bg: 'blue-grey',
    text: 'blue-grey',
  },
  blueGreyDarken: {
    bg: 'blue-grey darken-2',
    text: 'blue-grey lighten-3',
  },
})

export const ICON_POSITIONS = Object.freeze({
  left: 'left',
  right: 'right',
  default: 'left',
})

export const SIZES = Object.freeze({
  'x-small': 'x-small',
  small: 'small',
  large: 'large',
  'x-large': 'x-large',
  default: 'default',
})

export default {
  name: 'Button',

  inheritAttrs: false,

  props: {
    id: {
      type: String,
      required: false,
    },
    text: {
      type: String,
      required: false,
    },
    truncate: {
      type: Boolean,
      required: false,
    },
    maxWidth: {
      type: Number,
      required: false,
    },
    color: {
      type: String,
      required: false,
      default: () => 'default',
    },
    icon: {
      type: String,
      required: false,
    },
    elevation: {
      type: String,
      required: false,
      default: '0',
    },
    iconPosition: {
      type: String,
      required: false,
      default: 'left',
    },
    size: {
      type: String,
      required: false,
      default: 'default',
      validator: size => {
        return !size || Boolean(SIZES[size])
      },
    },
    to: {
      type: [String, Object],
      required: false,
    },
    loading: Boolean,
    disabled: Boolean,
    fab: Boolean,
    plain: Boolean,
    outlined: Boolean,
    block: Boolean,
    suppressActiveStyle: Boolean,
    href: String,
    target: String,
    depressed: Boolean,
    rounded: {
      type: Boolean,
      default: true,
    },
    tooltip: String,
  },

  data() {
    return {
      ICON_POSITIONS,
      SIZES,
    }
  },

  computed: {
    buttonColor() {
      return COLORS[this.color]?.bg ?? COLORS.default.bg
    },

    buttonClasses() {
      return {
        'px-4': !this.isCircleButton && !this.plain,
        'custom-button--suppress-active-style': this.suppressActiveStyle,
      }
    },

    isCircleButton() {
      return this.fab || (this.icon && !this.text)
    },

    textColor() {
      const isInverted = this.plain || this.outlined
      const attr = isInverted ? 'bg' : 'text'

      return COLORS[this.color]?.[attr] ?? COLORS.default[attr]
    },

    textColorClass() {
      if (!this.textColor) return ''

      const MODIFIER_PATTERN = /^(darken|lighten|accent)-\d+$/

      return this.textColor
        .split(' ')
        .map(token =>
          MODIFIER_PATTERN.test(token) ? `text--${token}` : `${token}--text`
        )
        .join(' ')
    },
  },
}
</script>

<style lang="scss">
.custom-button {
  &:visited {
    color: inherit;
  }
}

.custom-button.v-btn--disabled {
  opacity: 0.7;
}

.custom-button.v-btn--disabled.v-btn--has-bg {
  background-color: $color-blue-grey-darken1 !important;
  opacity: 0.7;
  color: white !important;
  .v-icon {
    color: white !important;
  }
}

.custom-button.v-btn--disabled.v-btn--outlined {
  border: 1px solid $color-blue-grey-lighten1;
  opacity: 0.7;
  color: $color-blue-grey-lighten1 !important;
}

.custom-button.v-btn--disabled.v-btn--plain {
  opacity: 0.7;
  color: $color-blue-grey-lighten1 !important;
}

.custom-button.v-btn--plain.blue-grey--text {
  span.button-text,
  i {
    color: $color-blue-grey-darken2 !important;
  }
}

.custom-button.custom-button--suppress-active-style {
  &.v-btn--active,
  &.v-btn__active {
    background-color: transparent !important;

    &::before {
      opacity: 0 !important;
    }

    .button-text,
    .v-icon {
      color: inherit !important;
    }
  }
}
</style>
